<script>
import bem from '@/utils/bem';
import PencilIcon from '@/components/svg/PencilIcon/PencilIcon.vue';
import EditTitleWithLinesModal from '@/components/pageContent/modules/EditTitleWithLinesModal.vue';
import EditTypesListModal from '@/components/pageContent/modules/EditTypesListModal.vue';
import publishGuardMixin from '@/mixins/publishGuardMixin';
import TitleWithLinesBlock from './title-with-lines-block.vue';
import styles from './digitize-types-list-style.scss';

const b = bem('digitize-types-list', styles);

export default {
  mixins: [publishGuardMixin],
  name: 'DigitizeTypesList',
  components: {
    PencilIcon,
    EditTitleWithLinesModal,
    EditTypesListModal,
    TitleWithLinesBlock,
  },
  props: {
    titleData: {
      type: Object,
      default: () => ({}),
    },
    typesList: {
      type: Array,
      default: () => [],
    },
    isMobileView: {
      type: Boolean,
      default: false,
    },
    isEditMode: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['update-title', 'update-types'],
  data() {
    return {
      showAll: false,
      currentSlide: 0,
      titleModalVisible: false,
      typesModalVisible: false,
    };
  },
  computed: {
    b() {
      return b;
    },
    textList() {
      return this.titleData?.textList || [];
    },
    hasTitle() {
      return this.textList.length > 0;
    },
    visibleItems() {
      if (this.isMobileView && !this.showAll) {
        return this.typesList.slice(0, 4);
      }
      return this.typesList;
    },
    showSeeMoreButton() {
      return this.isMobileView && this.typesList.length > 4;
    },
    showArrows() {
      return !this.isMobileView && this.typesList.length > 4;
    },
    maxSlide() {
      return Math.max(0, this.typesList.length - 4);
    },
  },
  methods: {
    // BEM helper
    bemClass(element, modifiers) {
      return b(element, modifiers);
    },
    // Toggle see more/less
    toggleShowAll() {
      this.showAll = !this.showAll;
    },
    // Slider navigation
    handlePrev() {
      if (this.currentSlide > 0) {
        this.currentSlide -= 1;
      }
    },
    handleNext() {
      if (this.currentSlide < this.maxSlide) {
        this.currentSlide += 1;
      }
    },
    // Get slide transform
    getSliderTransform() {
      // Each item is ~280px + 60px gap = 340px
      const itemWidth = 340;
      return `translateX(-${this.currentSlide * itemWidth}px)`;
    },
    // Get description based on view
    getDescription(item) {
      if (this.isMobileView && item.descriptionMob) {
        return item.descriptionMob;
      }
      return item.description || '';
    },
    // Get image based on view
    getImage(item) {
      if (this.isMobileView && item.imageMob) {
        return item.imageMob;
      }
      return item.image || '';
    },
    // Modal handlers
    openTitleModal() {
      if (!this.guardPublished()) return;
      if (this.isEditMode) {
        this.titleModalVisible = true;
      }
    },
    handleTitleSave(values) {
      this.$emit('update-title', values);
      this.titleModalVisible = false;
    },
    handleTitleDelete() {
      this.$emit('update-title', []);
      this.titleModalVisible = false;
    },
    openTypesModal() {
      if (!this.guardPublished()) return;
      if (this.isEditMode) {
        this.typesModalVisible = true;
      }
    },
    handleTypesSave(typesList) {
      this.$emit('update-types', typesList);
      this.typesModalVisible = false;
    },
  },
};
</script>

<template>
  <article :class="b({ isMobileView, isEditMode })">
    <!-- Title -->
    <div
      :class="b('title-wrapper', { editable: isEditMode })"
      role="button"
      tabindex="0"
      @click="openTitleModal"
      @keyup.enter="openTitleModal"
    >
      <TitleWithLinesBlock
        v-if="hasTitle"
        :class="b('title')"
        :is-mobile-view="isMobileView"
        :text-list="textList"
      />
      <div v-else-if="isEditMode" :class="b('add-field')">
        + Add title
      </div>
      <PencilIcon
        v-if="isEditMode && hasTitle"
        :class="b('edit-icon')"
        color="#fff"
        :size="20"
      />
    </div>

    <!-- Edit cards button (edit mode only) -->
    <button
      v-if="isEditMode"
      type="button"
      :class="b('edit-types-btn')"
      @click="openTypesModal"
    >
      <PencilIcon color="#293241" :size="16" />
      Edit cards
    </button>

    <!-- Desktop: Slider -->
    <div v-if="!isMobileView" :class="b('slider-wrapper', { center: !showArrows})">
      <!-- Left Arrow -->
      <button
        v-if="showArrows"
        type="button"
        :class="b('arrow-button', { left: true })"
        :disabled="currentSlide === 0"
        aria-label="Previous slide"
        @click="handlePrev"
      >
        <svg width="21" height="32" viewBox="0 0 21 32" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path
            d="M20 1L2 16L20 31"
            stroke="white"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
      </button>

      <!-- Slider Container -->
      <div :class="b('slider-container')">
        <div
          :class="b('slider')"
          :style="{ transform: getSliderTransform() }"
        >
          <div
            v-for="item in typesList"
            :key="item.id"
            :class="b('slide')"
          >
            <component
              :is="item.link ? 'a' : 'div'"
              :href="item.link || undefined"
              :class="b('item')"
            >
              <div :class="b('item-image')">
                <img
                  v-if="getImage(item)"
                  :src="getImage(item)"
                  :alt="item.title"
                />
                <div v-else :class="b('image-placeholder')">
                  {{ item.title }}
                </div>
              </div>
              <div :class="b('item-content')">
                <h3 :class="b('item-title', { 'no-link': !item.link })">
                  {{ item.title }}
                </h3>
                <p :class="b('item-description')">
                  {{ getDescription(item) }}
                </p>
              </div>
            </component>
          </div>
        </div>
      </div>

      <!-- Right Arrow -->
      <button
        v-if="showArrows"
        type="button"
        :class="b('arrow-button', { right: true })"
        :disabled="currentSlide >= maxSlide"
        aria-label="Next slide"
        @click="handleNext"
      >
        <svg width="21" height="32" viewBox="0 0 21 32" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path
            d="M1 1L19 16L1 31"
            stroke="white"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
      </button>
    </div>

    <!-- Mobile: Vertical List -->
    <template v-else>
      <div :class="b('mobile-list')">
        <div
          v-for="item in visibleItems"
          :key="item.id"
          :class="b('mobile-item')"
        >
          <component
            :is="item.link ? 'a' : 'div'"
            :href="item.link || undefined"
            :class="b('item')"
          >
            <div :class="b('item-image')">
              <img
                v-if="getImage(item)"
                :src="getImage(item)"
                :alt="item.title"
              />
              <div v-else :class="b('image-placeholder')">
                {{ item.title }}
              </div>
            </div>
            <div :class="b('item-content')">
              <h3 :class="b('item-title', { 'no-link': !item.link })">
                {{ item.title }}
              </h3>
              <p :class="b('item-description')">
                {{ getDescription(item) }}
              </p>
            </div>
          </component>
        </div>
      </div>

      <!-- See More Button -->
      <button
        v-if="showSeeMoreButton"
        type="button"
        :class="b('see-more-button')"
        @click="toggleShowAll"
      >
        {{ showAll ? 'see less' : 'see more' }}
      </button>
    </template>

    <!-- Title Modal -->
    <EditTitleWithLinesModal
      :visible="titleModalVisible"
      :data="textList"
      :show-delete="textList.length > 0"
      @save="handleTitleSave"
      @cancel="titleModalVisible = false"
      @delete="handleTitleDelete"
    />

    <!-- Types List Modal -->
    <EditTypesListModal
      :visible="typesModalVisible"
      :data="typesList"
      @save="handleTypesSave"
      @cancel="typesModalVisible = false"
    />
  </article>
</template>
